import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ShieldCheck, UserPlus, Loader2, RefreshCw, Copy, Check,
  AlertTriangle, Mail, KeyRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_EMAILS = new Set([
  "justine.baumgaertner@gmail.com",
  "sina.sebian@empoweryoursales.de",
  "andreawagner89@yahoo.de",
  "ela.tuk@gmx.de",
]);

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user && ADMIN_EMAILS.has((user.email || "").toLowerCase());

  // Provision form
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("viralcut1");
  const [plan, setPlan] = useState("yearly");
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Webhook events
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  useEffect(() => {
    if (isAdmin) loadEvents();
    // eslint-disable-next-line
  }, [isAdmin]);

  const loadEvents = async () => {
    setLoadingEvents(true);
    try {
      const { data } = await axios.get(`${API}/admin/ablefy/events?limit=20`);
      setEvents(data.events || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Events konnten nicht geladen werden");
    } finally {
      setLoadingEvents(false);
    }
  };

  const provision = async () => {
    if (!email.trim() || !email.includes("@")) {
      toast.error("Bitte gültige E-Mail eingeben"); return;
    }
    if (password && password.length < 6) {
      toast.error("Passwort muss mind. 6 Zeichen haben"); return;
    }
    setSubmitting(true);
    setLastResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/users/provision`, {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        plan,
        password: password.trim(),
      });
      setLastResult(data);
      toast.success(
        data.status === "created" ? "User angelegt!" :
        data.status === "updated" ? "User aktualisiert!" : "Fertig"
      );
      // refresh events to surface anything new
      loadEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Anlegen");
    } finally {
      setSubmitting(false);
    }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Kopieren fehlgeschlagen"); }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center px-4" data-testid="admin-no-access">
        <div className="max-w-md text-center space-y-3">
          <AlertTriangle className="w-10 h-10 mx-auto" style={{ color: '#FF6F61' }} />
          <h1 className="text-xl font-bold text-white">Kein Zugriff</h1>
          <p className="text-sm text-zinc-400">Diese Seite ist nur für Admins.</p>
          <Button variant="outline" onClick={() => navigate('/dashboard')} className="border-zinc-700 text-zinc-300">
            Zurück zum Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="admin-users-page">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}
                  className="h-8 w-8 text-zinc-400 hover:text-white" data-testid="admin-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <ShieldCheck className="w-5 h-5" style={{ color: '#C8FF4D' }} />
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Admin · User-Verwaltung
          </h1>
        </div>

        {/* Provision form */}
        <div className="rounded-xl bg-zinc-900/40 border border-zinc-800 p-5 space-y-4 mb-8">
          <div className="flex items-center gap-2">
            <UserPlus className="w-4 h-4" style={{ color: '#00D4FF' }} />
            <h2 className="text-sm font-semibold text-white">User manuell anlegen</h2>
          </div>
          <p className="text-xs text-zinc-400 leading-relaxed">
            Wenn ein Käufer nicht automatisch erkannt wurde (z.B. Webhook hat Ablefy nicht erreicht),
            kannst du ihn hier direkt anlegen. Mit Passwort: User kann sich sofort einloggen.
            Ohne Passwort: User bekommt einen Aktivierungs-Link.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-400 mb-1.5 block">E-Mail</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder="kunde@example.com"
                     className="bg-zinc-950 border-zinc-800 text-white text-sm h-9"
                     data-testid="admin-email-input" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400 mb-1.5 block">Name (optional)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                     placeholder="Vor- und Nachname"
                     className="bg-zinc-950 border-zinc-800 text-white text-sm h-9"
                     data-testid="admin-name-input" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400 mb-1.5 block">Passwort (mind. 6 Zeichen)</Label>
              <Input value={password} onChange={(e) => setPassword(e.target.value)}
                     placeholder="z.B. viralcut1"
                     className="bg-zinc-950 border-zinc-800 text-white text-sm h-9 font-mono"
                     data-testid="admin-password-input" />
              <p className="text-[10px] text-zinc-500 mt-1">User kann es später selbst ändern. Leer lassen → Aktivierungs-Link wird erzeugt.</p>
            </div>
            <div>
              <Label className="text-xs text-zinc-400 mb-1.5 block">Plan</Label>
              <div className="flex gap-1">
                {[
                  { id: 'yearly', label: 'Lifetime (yearly)' },
                  { id: 'monthly', label: 'Monthly' },
                ].map(p => (
                  <button key={p.id} onClick={() => setPlan(p.id)}
                          className={`flex-1 py-2 rounded-md text-xs font-medium transition-colors ${plan === p.id ? 'text-black' : 'bg-zinc-900 text-zinc-400 border border-zinc-800'}`}
                          style={plan === p.id ? { background: '#00D4FF' } : {}}
                          data-testid={`admin-plan-${p.id}`}>{p.label}</button>
                ))}
              </div>
            </div>
          </div>

          <Button onClick={provision} disabled={submitting || !email.trim()}
                  className="w-full text-black font-semibold h-10"
                  style={{ background: 'linear-gradient(135deg, #00D4FF 0%, #C8FF4D 100%)' }}
                  data-testid="admin-provision-btn">
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Lege an...</> : <><UserPlus className="w-4 h-4 mr-2" />User anlegen / aktualisieren</>}
          </Button>

          {lastResult && (
            <div className="rounded-lg p-3 mt-2"
                 style={{ background: 'rgba(200,255,77,0.06)', border: '1px solid rgba(200,255,77,0.3)' }}
                 data-testid="admin-last-result">
              <p className="text-xs font-semibold mb-2" style={{ color: '#C8FF4D' }}>
                {lastResult.status === 'created' ? '✓ User angelegt' : '✓ User aktualisiert'} · plan={lastResult.plan}
              </p>
              <div className="space-y-1.5 text-xs">
                <div className="flex items-center gap-2">
                  <Mail className="w-3 h-3 text-zinc-500" />
                  <span className="text-zinc-300">{lastResult.email}</span>
                </div>
                {lastResult.password_set && (
                  <p className="text-[11px] text-zinc-400">User kann sich direkt mit dem von dir gesetzten Passwort anmelden.</p>
                )}
                {lastResult.activation_code && !lastResult.password_set && (
                  <>
                    <div className="flex items-center gap-2">
                      <KeyRound className="w-3 h-3 text-zinc-500" />
                      <span className="font-mono text-zinc-300 text-xs">{lastResult.activation_code}</span>
                    </div>
                    {lastResult.activation_link && (
                      <button onClick={() => copyText(lastResult.activation_link)}
                              className="flex items-center gap-1.5 text-xs"
                              style={{ color: '#00D4FF' }}
                              data-testid="admin-copy-link-btn">
                        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                        {copied ? 'kopiert' : 'Activation-Link kopieren'}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Webhook events */}
        <div className="rounded-xl bg-zinc-900/40 border border-zinc-800 p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" style={{ color: '#7DD8F4' }} />
              Letzte Ablefy-Webhooks
            </h2>
            <button onClick={loadEvents} disabled={loadingEvents}
                    className="text-xs text-zinc-400 hover:text-white flex items-center gap-1"
                    data-testid="admin-refresh-events-btn">
              <RefreshCw className={`w-3 h-3 ${loadingEvents ? 'animate-spin' : ''}`} />
              Aktualisieren
            </button>
          </div>
          {events.length === 0 && !loadingEvents && (
            <p className="text-xs text-zinc-500">Noch keine Ablefy-Events erfasst.</p>
          )}
          <ul className="space-y-1.5" data-testid="admin-events-list">
            {events.map((e, i) => {
              const colors = {
                created: '#C8FF4D', updated: '#7DD8F4',
                ignored: '#9A9A9F', error: '#FF6F61', received: '#9A9A9F',
              };
              const color = colors[e.outcome] || '#9A9A9F';
              return (
                <li key={i} className="rounded-md border border-zinc-800 bg-zinc-950 p-2.5 text-xs"
                    data-testid={`admin-event-${i}`}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={{ background: `${color}1a`, color, border: `1px solid ${color}66` }}>
                      {e.outcome}
                    </span>
                    <span className="text-[10px] text-zinc-500 font-mono">{(e.received_at || '').slice(0, 19).replace('T', ' ')}</span>
                  </div>
                  <p className="text-zinc-300 leading-relaxed break-words">{e.outcome_detail || '—'}</p>
                  {e.parsed?.email && (
                    <p className="text-[10px] text-zinc-500 mt-1">payload-email: {e.parsed.email}</p>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
